import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

const CopyIcon = ({ copied }) => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    {copied ? (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M5 13l4 4L19 7"
      />
    ) : (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    )}
  </svg>
);

const RunQueryIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const CodeBlockWithActions = ({ language, children, isDarkMode }) => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const codeContent = String(children).replace(/\n$/, '');
  const isSqlBlock = language === 'sql';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleRunQuery = () => {
    navigate('/query', { state: { query: codeContent } });
  };

  return (
    <div className="relative group my-4">
      {/* Action buttons container */}
      <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity z-10">
        {/* Copy button - always visible */}
        <button
          onClick={handleCopy}
          className="p-2 rounded bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors shadow-sm"
          title={copied ? "Copied!" : "Copy code"}
          aria-label="Copy code to clipboard"
        >
          <CopyIcon copied={copied} />
        </button>

        {/* Run SQL button - only for SQL blocks */}
        {isSqlBlock && (
          <button
            onClick={handleRunQuery}
            className="p-1.5 rounded bg-primary-600 dark:bg-primary-500 text-white hover:bg-primary-700 dark:hover:bg-primary-400 transition-colors shadow-sm"
            title="Run this SQL query"
            aria-label="Run SQL query"
          >
            <RunQueryIcon />
          </button>
        )}
      </div>

      {/* Syntax highlighted code */}
      <SyntaxHighlighter
        style={isDarkMode ? vscDarkPlus : prism}
        language={language}
        PreTag="div"
        customStyle={{
          padding: '1rem',
          paddingRight: '3rem',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          margin: 0,
          maxWidth: '100%',
          overflow: 'auto',
        }}
      >
        {codeContent}
      </SyntaxHighlighter>
    </div>
  );
};

export default CodeBlockWithActions;
